"use client"

import type { AddOnId } from "@/lib/quiz/types"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"

export interface RecommendationAddOn {
  id: AddOnId
  name: string
  /** Localized price label, already formatted (`"CHF 470"`). For FREE add-ons,
   *  callers set this to the localized free word (e.g. "Free"). */
  priceLabel: string
  /** Localized member price (`"CHF 119"`) or the "member price" fallback label.
   *  When set (and not free), the row shows the standard price struck/greyed +
   *  this in green. Undefined on the control card. */
  memberPriceLabel?: string
  /** Suffix after a member-price FIGURE, e.g. "member". Omit for the fallback label. */
  memberSuffix?: string
  isFree?: boolean
  infoSlot?: React.ReactNode
  /** Thumbnail at the left of the tile (product image / blood-test photo). */
  iconSlot?: React.ReactNode
}

interface Props {
  name: string
  baseInfoSlot?: React.ReactNode
  /**
   * Small circular icon rendered inline-left of the title. Should be
   * sized so it doesn't exceed the title's line height (~28px).
   */
  icon?: React.ReactNode
  /** Pill-shaped label anchored to the top-right inside the card. */
  badge?: string
  /**
   * One-liner explaining WHY the base package was recommended.
   * Rendered as a soft-green eyebrow above the title so the
   * justification rides with the package.
   */
  whyReason?: string
  /** Super-short description, one line. */
  description: string
  /**
   * Formatted price. Omit when the market has no authoritative figure for this
   * package (EUR markets deliberately ship no MRI prices while imaging is
   * coming-soon — see FALLBACK_PRICES_EUR). Pair the omission with
   * {@link priceComingSoonLabel} so the slot says the price isn't known yet
   * instead of going blank.
   */
  price?: string
  /**
   * Rendered in the price slot when {@link price} is omitted, e.g. "Price
   * coming soon". Says out loud that we don't have a figure yet, which beats
   * both an invented placeholder and an empty gap.
   */
  priceComingSoonLabel?: string
  currency?: string
  /** Strikethrough total before insurance reimbursement. */
  originalPrice?: string
  /** Footnote under the price. */
  priceNote?: string
  /** Muted parenthetical rendered inline-right of the big price, e.g.
   *  "package + recommended add-ons". Hidden when undefined. */
  priceBreakdownNote?: string
  /** Top 3 highlights from the base package, rendered with green checkmarks. */
  features: string[]
  /** Optional — when omitted, the add-ons section + label don't render. */
  addOns?: RecommendationAddOn[]
  /** Translated heading for the add-ons section (e.g. "Add-ons"). */
  addOnsLabel: string
  /** One-sentence summary rendered below the add-ons label,
   *  explaining at a glance why these add-ons are in the cart. */
  addOnsSummary?: string
  onRemoveAddOn?: (id: AddOnId) => void
  ctaLabel: string
  ctaHref: string
  /** Click handler on the CTA link — the container attaches tracking here;
   *  the card itself stays presentational. */
  onCtaClick?: () => void
  /**
   * When set, the package isn't bookable in this market yet, so the CTA
   * renders as a button that runs this (opens the waitlist modal) instead of
   * a booking link. `ctaHref` is ignored in that case; pass a waitlist-flavoured
   * `ctaLabel` alongside it.
   */
  onWaitlistClick?: () => void
  /** "Coming soon" pill rendered next to the package title. */
  comingSoonLabel?: string
}

const TrashIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

/** A single add-on tile. Presentational — shared between the control
 *  recommendation card and the side-by-side membership variant. Renders one of
 *  three price treatments: free (the localized free word, no trash button),
 *  dual standard/member price (standard struck + member in green), or a plain
 *  standard price. */
export function AddOnRow({
  addOn,
  onRemove,
}: {
  addOn: RecommendationAddOn
  onRemove?: (id: AddOnId) => void
}) {
  return (
    <li className="flex items-center gap-2.5 rounded-[12px] border border-neutral-200 bg-white py-2 pr-2.5 pl-2">
      {addOn.iconSlot}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="type-body-md font-medium text-neutral-900">
            {addOn.name}
          </span>
          {addOn.infoSlot && (
            <span className="-my-2 inline-flex">{addOn.infoSlot}</span>
          )}
        </div>
      </div>
      {addOn.isFree ? (
        <span className="type-body-md mr-2 flex-shrink-0 font-medium text-neutral-900">
          {addOn.priceLabel}
        </span>
      ) : addOn.memberPriceLabel ? (
        <span className="flex-shrink-0 text-right">
          <span className="type-body-sm block text-neutral-400 line-through">
            {addOn.priceLabel}
          </span>
          <span className="type-body-sm text-deep-green-700 block font-medium whitespace-nowrap">
            {addOn.memberPriceLabel}
            {addOn.memberSuffix && (
              <span className="text-neutral-400"> {addOn.memberSuffix}</span>
            )}
          </span>
        </span>
      ) : (
        <span className="type-body-md flex-shrink-0 font-medium text-neutral-900">
          {addOn.priceLabel}
        </span>
      )}
      {onRemove && !addOn.isFree && (
        <button
          type="button"
          onClick={() => onRemove(addOn.id)}
          aria-label={`Remove ${addOn.name}`}
          className="ml-0.5 inline-flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
        >
          {TrashIcon}
        </button>
      )}
    </li>
  )
}

/** "Why this?" justification — a soft, low-saturation blue callout
 *  with a small sparkle glyph, signalling a personalised / smart
 *  pick. Used for the package recommendation reason and the add-ons
 *  summary.
 *
 *  `bleed` pulls the box out by its own horizontal padding (`-mx-3`)
 *  so the sparkle aligns with the card's content edge — i.e. in line
 *  with the benefit checkmarks below it. Used for the main package
 *  reason; the add-ons summary stays inset (default). */
export function WhyEyebrow({
  children,
  bleed = false,
}: {
  children: React.ReactNode
  bleed?: boolean
}) {
  return (
    <div
      className={cn(
        "bg-light-blue-50 text-light-blue-800 rounded-[12px] px-3 py-2",
        bleed && "-mx-[7px]"
      )}
    >
      <p className="type-body-md">
        {/* Inline sparkle prefix — wrapped lines flow back to the
            box's left edge (no hanging indent under the first word). */}
        <span aria-hidden="true" className="text-light-blue-500 mr-1.5 text-xs">
          ✦
        </span>
        {children}
      </p>
    </div>
  )
}

/**
 * The personalised recommendation card on the quiz result. Mirrors
 * the pricing-page `PricingCard` shell: semi-transparent white
 * background that fades to solid white on hover, soft border, shadow.
 */
export function RecommendationCard({
  name,
  baseInfoSlot,
  icon,
  badge,
  whyReason,
  description,
  price,
  currency = "CHF",
  originalPrice,
  priceNote,
  features,
  addOns,
  addOnsLabel,
  addOnsSummary,
  onRemoveAddOn,
  ctaLabel,
  ctaHref,
  onCtaClick,
  onWaitlistClick,
  comingSoonLabel,
  priceComingSoonLabel,
  priceBreakdownNote,
}: Props) {
  const visibleAddOns = addOns ?? []
  return (
    <article
      className={cn(
        "relative rounded-[16px] border border-white bg-white/80 p-6 md:p-8",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-[250ms]",
        "hover:bg-white hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)]"
      )}
    >
      {/* Header — title and the "Recommended" chip share one flex row so a
        long title (e.g. "Advanced blood test") wraps in its own column
        instead of running under an absolutely-positioned badge. The badge
        stays pinned top-right via `flex-shrink-0`; `items-start` keeps it on
        the first line as the title grows. */}
      <header>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {icon}
            <h2 className="type-headline-lg font-medium text-neutral-900">
              {name}
            </h2>
            {baseInfoSlot && (
              <span className="mt-1 -ml-1 inline-block">{baseInfoSlot}</span>
            )}
          </div>
          {badge && (
            <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block flex-shrink-0 rounded-full px-5 py-1 font-medium normal-case">
              {badge}
            </span>
          )}
        </div>
        {/* Availability pill on its own row rather than inside the title flex.
          Inside it, the row's `items-center` vertically centred the pill against
          a wrapped two-line package name ("Advanced MRI"), leaving it floating
          beside the second line. Its own row is wrap-proof. */}
        {comingSoonLabel && (
          <div className="mt-2.5">
            <ComingSoonPill label={comingSoonLabel} />
          </div>
        )}
        <p className="type-body-md mt-2 text-neutral-600">{description}</p>
      </header>

      {/* "Why this package?" justification — sits between the package
        summary and the highlights so it reads as the reason the
        features below were picked, rather than a header-level eyebrow. */}
      {whyReason && (
        <div className="mt-3">
          <WhyEyebrow bleed>{whyReason}</WhyEyebrow>
        </div>
      )}

      {/* Highlights */}
      {features.length > 0 && (
        <ul className="mt-5 space-y-2">
          {features.slice(0, 3).map((label) => (
            <li
              key={label}
              className="flex items-start gap-2.5 text-neutral-800"
            >
              <CheckSvg />
              <span className="type-body-md">{label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Price. When the market has no authoritative figure we say so in the
        slot rather than inventing a number or leaving a gap. */}
      {price === undefined && priceComingSoonLabel && (
        <p className="type-title-md mt-6 text-neutral-500">
          {priceComingSoonLabel}
        </p>
      )}
      {price !== undefined && (
        <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {originalPrice && (
            <span className="type-body-md text-neutral-400 line-through">
              <span className="type-body-sm font-medium">{currency}</span>{" "}
              {originalPrice}
            </span>
          )}
          <span className="type-headline-md font-medium text-neutral-900">
            <span className="type-body-md font-medium text-neutral-500">
              {currency}
            </span>{" "}
            {price}
          </span>
          {priceBreakdownNote && (
            <span className="type-body-md text-neutral-500">
              ({priceBreakdownNote})
            </span>
          )}
        </div>
      )}
      {priceNote && (
        <p className="type-body-sm mt-1 text-neutral-600">{priceNote}</p>
      )}

      {/* CTA — directly below the price; acts as the divider too,
        so we don't render a horizontal rule above the add-ons.

        Two shapes. When the recommended package is bookable this is a link
        into the booking app. When the market hasn't opened the category yet
        (`coming_soon` / `waitlist`) it becomes a button that opens the
        waitlist modal instead — a booking deep-link for a product the market
        can't sell is the one outcome worse than no CTA. Same treatment every
        other surface gives a coming-soon product. */}
      {onWaitlistClick ? (
        <button
          type="button"
          onClick={onWaitlistClick}
          className={cn(
            buttonVariants({ variant: "primary", size: "lg", shape: "round" }),
            "group mt-5 w-full cursor-pointer gap-2"
          )}
        >
          <span>{ctaLabel}</span>
          <span className="inline-block transition-transform duration-200 ease-out group-hover:translate-x-1">
            <ArrowRightIcon />
          </span>
        </button>
      ) : (
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onCtaClick}
          className={cn(
            buttonVariants({ variant: "primary", size: "lg", shape: "round" }),
            "group mt-5 w-full gap-2"
          )}
        >
          <span>{ctaLabel}</span>
          <span className="inline-block transition-transform duration-200 ease-out group-hover:translate-x-1">
            <ArrowRightIcon />
          </span>
        </a>
      )}

      {/* Add-ons */}
      {visibleAddOns.length > 0 && (
        <>
          <p className="type-label-lg mt-8 text-neutral-500">{addOnsLabel}</p>
          {addOnsSummary && (
            <div className="mt-2 mb-3">
              <WhyEyebrow>{addOnsSummary}</WhyEyebrow>
            </div>
          )}
          <ul className="mt-3 space-y-2">
            {visibleAddOns.map((addOn) => (
              <AddOnRow key={addOn.id} addOn={addOn} onRemove={onRemoveAddOn} />
            ))}
          </ul>
        </>
      )}
    </article>
  )
}

/** Olive check used for the 3 base-package highlights. Matches the
 *  CheckCircleIcon used elsewhere on the marketing site. */
function CheckSvg() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="mt-0.5 flex-shrink-0"
    >
      <circle cx="8" cy="8" r="8" fill="#95A73B" />
      <path
        d="M5 8l2 2 4-4"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
